import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ClipboardService {
  async copyText(text: string): Promise<boolean> {
    if (!text) {
      return false;
    }

    if (await this.copyWithCapacitor(text)) {
      return true;
    }

    if (await this.copyWithCordova(text)) {
      return true;
    }

    if (await this.copyWithNavigator(text)) {
      return true;
    }

    return this.copyWithTextareaFallback(text);
  }

  private async copyWithCapacitor(text: string): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    const clipboardPlugin = (window as any)?.Capacitor?.Plugins?.Clipboard;
    if (!clipboardPlugin) {
      return false;
    }

    try {
      if (typeof clipboardPlugin.write === 'function') {
        await clipboardPlugin.write({ string: text });
        return true;
      }

      if (typeof clipboardPlugin.writeText === 'function') {
        await clipboardPlugin.writeText({ value: text });
        return true;
      }
    } catch (err) {
      console.warn('[ClipboardService] Capacitor clipboard copy failed, trying next path.', err);
    }

    return false;
  }

  private async copyWithCordova(text: string): Promise<boolean> {
    if (typeof window === 'undefined') {
      return false;
    }

    const cordovaClipboard = (window as any)?.cordova?.plugins?.clipboard;
    if (typeof cordovaClipboard?.copy !== 'function') {
      return false;
    }

    try {
      await new Promise<void>((resolve, reject) => {
        cordovaClipboard.copy(text, resolve, reject);
      });
      return true;
    } catch (err) {
      console.warn('[ClipboardService] Cordova clipboard copy failed, trying next path.', err);
      return false;
    }
  }

  private async copyWithNavigator(text: string): Promise<boolean> {
    if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') {
      return false;
    }

    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('[ClipboardService] Web clipboard copy failed, trying fallback.', err);
      return false;
    }
  }

  private copyWithTextareaFallback(text: string): boolean {
    if (typeof document === 'undefined' || !document.body) {
      return false;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch (err) {
      console.error('[ClipboardService] Clipboard fallback failed.', err);
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
